import React, { useState, useEffect } from "react";
import Navbar from "../components/layout/Navbar";
import Reveal from "../components/common/Reveal";
import Footer from "../components/layout/Footer";
import { SONGS, SongTab } from "../data/songs";
import PdfViewer from "../components/common/PdfViewer";


const getSongById = (id: string): SongTab | null => {
  return SONGS.find((song) => song.id === id) || null;
};

const SongDetailPage: React.FC = () => {
  const hash = window.location.hash;
  const params = new URLSearchParams(hash.split("?")[1] || "");
  const songId = params.get("id") || "";

  const song = getSongById(songId);
  const [zoom, setZoom] = useState<number>(100);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  // Listen for fullscreen changes - must be called before any early returns
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  if (!song) {
    return (
      <div className="min-h-screen bg-primary">
        <Navbar />
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <h1 className="mb-4 text-2xl font-semibold text-accentNavy">Song Not Found</h1>
            <a href="#resources" className="text-accentGold hover:underline">
              Back to Resources
            </a>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "Beginner":
        return "bg-green-100 text-green-700 border-green-200";
      case "Intermediate":
        return "bg-yellow-100 text-yellow-700 border-yellow-200";
      case "Advanced":
        return "bg-red-100 text-red-700 border-red-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 25, 50));
  };

  const handleResetZoom = () => {
    setZoom(100);
  };

  const pdfUrl = `/${song.pdfUrl}`;

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `${song.title} - ${song.artist}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleFullscreen = () => {
    const pdfContainer = document.getElementById("pdf-container");
    if (!pdfContainer) return;

    if (!isFullscreen) {
      if (pdfContainer.requestFullscreen) {
        pdfContainer.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
    setIsFullscreen(!isFullscreen);
  };

  return (
    <div className="min-h-screen bg-primary">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary via-accentGold/5 to-accentNavy/10 py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accentNavy/20 via-transparent to-accentGold/15" />
        <div className="absolute -right-32 top-1/2 h-96 w-96 rounded-full bg-accentGold/30 blur-3xl" />
        <div className="absolute -left-32 bottom-1/4 h-96 w-96 rounded-full bg-accentNavy/20 blur-3xl" />

        <div className="relative z-10 mx-auto max-w-6xl px-6 lg:px-10">
          <Reveal>
            <a
              href="#resources"
              className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-accentNavy/70 transition-colors duration-200 hover:text-accentGold"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back to Resources
            </a>
          </Reveal>

          <Reveal delay={120}>
            <div className="mb-6 flex items-center gap-3">
              <span className="rounded-full border border-accentNavy/20 bg-accentNavy/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-accentNavy/70">
                {song.category}
              </span>
              <span
                className={[
                  "rounded-full border px-4 py-1.5 text-xs font-semibold",
                  getDifficultyColor(song.difficulty),
                ].join(" ")}
              >
                {song.difficulty}
              </span>
            </div>
          </Reveal>

          <Reveal delay={180}>
            <h1 className="mb-3 font-display text-4xl leading-tight text-accentNavy sm:text-5xl lg:text-6xl">
              {song.title}
            </h1>
            <p className="text-xl text-accentNavy/70">{song.artist}</p>
          </Reveal>
        </div>
      </section>

      {/* PDF Viewer Section */}
      <section className="relative bg-gradient-to-b from-primary via-primary/95 to-primary/90 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          {/* Controls Bar */}
          <Reveal>
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-white/50 bg-white/90 p-4 shadow-[0_10px_30px_-15px_rgba(9,32,63,0.2)]">
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-accentNavy/70">Zoom:</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleZoomOut}
                    disabled={zoom <= 50}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-accentNavy/20 bg-white text-accentNavy transition-all duration-200 hover:border-accentGold hover:bg-accentGold hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Zoom out"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7"
                      />
                    </svg>
                  </button>
                  <span className="min-w-[3rem] text-center text-sm font-semibold text-accentNavy">
                    {zoom}%
                  </span>
                  <button
                    type="button"
                    onClick={handleZoomIn}
                    disabled={zoom >= 200}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-accentNavy/20 bg-white text-accentNavy transition-all duration-200 hover:border-accentGold hover:bg-accentGold hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Zoom in"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"
                      />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={handleResetZoom}
                    className="rounded-lg border border-accentNavy/20 bg-white px-3 py-1.5 text-xs font-semibold text-accentNavy transition-all duration-200 hover:border-accentGold hover:bg-accentGold hover:text-white"
                  >
                    Reset
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  className="flex items-center gap-2 rounded-lg border border-accentNavy/20 bg-white px-4 py-2 text-sm font-semibold text-accentNavy transition-all duration-200 hover:border-accentGold hover:bg-accentGold hover:text-white"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                    />
                  </svg>
                  {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                </button>
              </div>
            </div>
          </Reveal>

          {/* PDF Container */}
          <PdfViewer pdfUrl={pdfUrl} zoom={zoom} isFullscreen={isFullscreen} />

          {/* Download Section */}
          <Reveal delay={180}>
            <div className="mt-8 flex flex-col items-center gap-6 rounded-3xl border border-white/50 bg-white/90 p-8 shadow-[0_20px_50px_-30px_rgba(9,32,63,0.3)]">
              <div className="text-center">
                <h3 className="mb-2 text-xl font-semibold text-accentNavy">
                  Download This Tab
                </h3>
                <p className="text-sm text-accentNavy/70">
                  Save this ukulele tab to your device for offline practice
                </p>
              </div>
              <button
                type="button"
                onClick={handleDownload}
                className="inline-flex items-center gap-3 rounded-full border border-accentNavy/20 bg-accentNavy px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] text-white shadow-[0_18px_30px_-20px_rgba(9,32,63,0.7)] transition-all duration-300 hover:border-accentGold hover:bg-accentGold hover:shadow-[0_20px_45px_-18px_rgba(9,32,63,0.65)]"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Download PDF
              </button>
            </div>
          </Reveal>

          {/* Back Button */}
          <Reveal delay={220}>
            <div className="mt-8 flex items-center justify-center">
              <a
                href="#resources"
                className="inline-flex items-center gap-2 rounded-full border border-accentNavy/20 bg-white/80 px-6 py-3 text-sm font-semibold uppercase tracking-[0.15em] text-accentNavy transition-all duration-300 hover:border-accentGold hover:bg-accentGold hover:text-white"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Back to Resources
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SongDetailPage;


